import { IEventRegistry } from 'app/shared/events/event_registry';

interface Listener {
    fn: Function;
    destroy: Function;
}

export class EventRegistryProxy implements IEventRegistry {
    private readonly listeners: Set<Listener>;
    private target: IEventRegistry;

    /**
     * An event registry which acts as a proxy to other event registries! Madness!
     * This is useful when you need to swap out the underlying event registry and want to transfer all
     * the current listeners. This class handles exactly that.
     * You can expose this event registry like a normal event registry registry,
     * but then call {@link EventRegistryProxy#setTarget} whenever you need to replace the proxied registry.
     */
    constructor() {
        this.listeners = new Set();
        this.target = undefined;
    }

    addEventListener(fn: (...args: any[]) => any): () => void {
        // add our listener if we have one
        const destroy = this.target ? this.target.addEventListener(fn) : () => undefined;
        // setup our cached listener object
        const listener = { fn, destroy };
        // save it
        this.listeners.add(listener);
        return () => {
            // when we ask to destroy this listener, make sure to remove it from the current target
            listener.destroy();
            // and delete it from our list
            this.listeners.delete(listener);
        };
    }

    async notifyListeners(...args: Array<any>): Promise<void> {
        // just apply each of our listeners!
        await this.listeners.forEach((l) => l.fn(...args));
    }

    /**
     * Set the target event registry
     * This takes all the current listeners and transfers them to the new event registry
     * @param eventRegistry
     */
    setTarget(eventRegistry: IEventRegistry): void {
        // set our new target
        this.target = eventRegistry;
        // then update each cached listener
        this.listeners.forEach((listener) => {
            // destroy it from the old target
            listener.destroy();
            // and add it to the new target (resetting the rm function)
            listener.destroy = this.target.addEventListener(listener.fn);
        });
    }

    /**
     * Exposed just for testing
     */
    getListenerCount(): number {
        return this.listeners.size;
    }
}
